import React from "react";
import "98.css";
import styled from "styled-components";
import PlayableSolitaire from "../PlayableSolitaire";
import SpectatedSolitaire from "../SpectatedSolitaire";
import Stage from "../Stage";
import useRoomLayout, { FALLBACK_NEIGHBOR_COUNT } from "../Stage/useRoomLayout";
import BootScreen from "../BootScreen";
import CrtOverlay from "../CrtOverlay";
import Screensaver from "../Screensaver";
import { applyStoredDesktopBackground } from "../DisplayProperties";
import { PRIORITY } from "../Stage/windowManager";
import useSolitaireServer from "../../hooks/useSolitaireServer";
import useCompactMode from "../../hooks/useCompactMode";
import { cardBackForGameId } from "../SpectatedSolitaire";

// Dev knob: ?quietbot[=N] spins up sacrificial neighbors that make one
// board move and then sit attached-but-silent — quiet-seat swap bait
// (see src/dev/quietBots.js). Lazy import: absent the param, the module
// never loads.
// Dev builds only: quiet bots open REAL server games — in prod that
// would let any shared ?quietbot URL pollute the permanent archive.
// (Parcel inlines NODE_ENV, so the whole branch — and the lazy module —
// falls out of production bundles.)
if (
  process.env.NODE_ENV !== "production" &&
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("quietbot")
) {
  import("../../dev/quietBots");
}

// The player's card back: `?back=7` dev override wins, then the deck
// chosen via the Game → Deck… dialog (localStorage), then the same
// per-game hash back that SPECTATORS see — so by default your deck
// matches what everyone else watching your game sees. (A custom choice
// still diverges for watchers until the protocol carries it.)
function readPlayerCardBack(gameId) {
  if (typeof window === "undefined") return "back-3";
  const raw = new URLSearchParams(window.location.search).get("back");
  if (raw) {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 0 && n <= 18) return `back-${n}`;
  }
  try {
    return localStorage.getItem("saat.cardBack") || cardBackForGameId(gameId);
  } catch {
    return cardBackForGameId(gameId); // storage denied — hashed default
  }
}

function App() {
  const compact = useCompactMode();
  const roomLayout = useRoomLayout(compact);
  const neighborCount = compact
    ? 0
    : (roomLayout?.neighbors.length ?? FALLBACK_NEIGHBOR_COUNT);
  const {
    myGameId,
    myGameMoves,
    myGameCanUndo,
    myGameResyncNonce,
    sendMove,
    sendUndo,
    sendCursor,
    dealNewGame,
    abandonGame,
    activeGameIds,
    watchedGameIds,
    departedWatchedIds,
    departWatchedGame,
    quietArrivalIds,
    gameGenerations,
    browseGameIds,
    browseEndedIds,
    browseReadyIds,
    browseFinalStates,
    roomActive,
    openGameBrowser,
    setBrowsedGames,
    loadBrowseFinal,
    closeGameBrowser,
    watchGame,
    unwatchGame,
    subscribeGame,
    idlePrompt,
    confirmStillHere,
    myGameTimedOut,
    myGameWon,
    netTrouble,
    dismissNetTrouble,
  } = useSolitaireServer({ neighborCount });

  // Wallpaper choice survives reloads.
  React.useEffect(() => {
    applyStoredDesktopBackground();
  }, []);
  // Gate on having a game, not on live socket status: once we're seated,
  // reconnect blips shouldn't unmount the Stage (that would replay the
  // whole opening choreography).
  if (myGameId == null) {
    // The POST screen doubles as the loading screen — the machine is
    // "booting" while the socket finds you a game.
    return (
      <Main>
        <CrtOverlay />
        <BootScreen waiting />
      </Main>
    );
  }


  // The one ambient notice, chosen by priority; first match wins. Add
  // new notices HERE, never as a separately-rendered dialog — Stage
  // renders this through the same modal slot as every other dialog.
  // Order: timed-out > net-trouble > still-there — when the connection
  // is down, "still there?" is a question whose answer we can't
  // deliver, so the network notice replaces it; on reconnect the
  // welcome's idle_ms mirror re-raises the prompt if it's still true.
  const systemNotice = myGameTimedOut
    ? {
        key: "timed-out",
        priority: PRIORITY.TIMED_OUT,
        title: "Solitaire Alone Together",
        icon: "question",
        message: "Timed out. Start a new game?",
        buttons: [{ label: "OK", onClick: dealNewGame }],
      }
    : netTrouble
      ? {
          key: "net-trouble",
          priority: PRIORITY.NET_TROUBLE,
          title: "Network Error",
          icon: "warning",
          message: "A network error has occurred. Trying to reconnect…",
          buttons: [{ label: "OK", onClick: dismissNetTrouble }],
          onCancel: dismissNetTrouble,
        }
      : idlePrompt
        ? {
            key: "still-there",
            priority: PRIORITY.STILL_THERE,
            title: "Solitaire Alone Together",
            icon: "question",
            // The one idle prompt: they're demonstrably present but
            // haven't moved a card in almost the whole window — say why
            // we're asking. Fully-idle players get the screensaver
            // instead, never a prompt.
            // Nolen's copy (2026-09-21): vague on purpose — no minute count.
            message: "You haven't made a move in a while, are you still there?",
            buttons: [{ label: "OK", onClick: confirmStillHere }],
          }
        : null;

  const title = `(You) Game #${myGameId} — Solitaire Alone Together`;

  const playerSlot = (
    <PlayableSolitaire
      // Nonce forces a rebuild from server history after a move_rejected
      // resync (the game ID alone wouldn't change).
      key={`${myGameId}:${myGameResyncNonce}`}
      title={title}
      gameId={myGameId}
      cardBack={readPlayerCardBack(myGameId)}
      sendMove={sendMove}
      sendUndo={sendUndo}
      sendCursor={sendCursor}
      dealNewGame={dealNewGame}
      initialMoves={myGameMoves}
      initialCanUndo={myGameCanUndo}
    />
  );

  const allSpectatedSlots = activeGameIds.map((id) => (
    <SpectatedSolitaire
      key={id}
      gameId={id}
      // Bumped when this id's machine is rebuilt from scratch (ghost
      // replays reuse historical numbers) — re-arms a frozen window.
      generation={gameGenerations.get(id) ?? 0}
      subscribeGame={subscribeGame}
      title={`Game #${id} — Solitaire Alone Together`}
    />
  ));
  const watchedIds = new Set(watchedGameIds);
  // Departed seats (their close ritual / cascade has fully played) hold
  // their place in the watched set for reconnect integrity, but their
  // windows are gone until the server redeals the slot.
  const neighborSlots = allSpectatedSlots.filter(
    (slot) =>
      watchedIds.has(Number(slot.key)) &&
      !departedWatchedIds.has(Number(slot.key)),
  );

  return (
    <Main>
      <CrtOverlay />
      <Screensaver />
      <Stage
        playerSlot={playerSlot}
        neighborSlots={neighborSlots}
        browserGameSlots={allSpectatedSlots}
        playerGameId={myGameId}
        playerGameOver={myGameTimedOut}
        playerGameWon={myGameWon}
        dealNewGame={dealNewGame}
        abandonGame={abandonGame}
        roomActive={roomActive}
        browseGameIds={browseGameIds}
        browseEndedIds={browseEndedIds}
        browseReadyIds={browseReadyIds}
        browseFinalStates={browseFinalStates}
        openGameBrowser={openGameBrowser}
        setBrowsedGames={setBrowsedGames}
        loadBrowseFinal={loadBrowseFinal}
        closeGameBrowser={closeGameBrowser}
        watchGame={watchGame}
        unwatchGame={unwatchGame}
        departGame={departWatchedGame}
        // Reconnect reseats of seats that died offline open silently:
        // waking shows a settled room, never a parade of arrivals.
        quietArrivalIds={quietArrivalIds}
        roomLayout={roomLayout}
        systemNotice={systemNotice}
      />
    </Main>
  );
}

const Main = styled.main``;

export default App;
